import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import PasswordInput from "../components/PasswordInput";
import logo from "../../src/assets/images/logo.png";
import worldmap from "../../src/assets/images/world-map.png";
import usa from "../../src/assets/images/usa.png";
import nl from "../../src/assets/images/nl.png";
import dashboardBG from "../../src/assets/images/background.png";

const USERNAME_MAX = 13;

const SignUp = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "username") {
      setFormData((p) => ({ ...p, username: value.slice(0, USERNAME_MAX) }));
      return;
    }
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const un = formData.username.trim();
    if (!un) {
      toast.error("Please enter a username");
      return;
    }
    if (un.length < 3) {
      toast.error("Username must be at least 3 characters");
      return;
    }
    if (un.length > USERNAME_MAX) {
      toast.error(`Username must be at most ${USERNAME_MAX} characters`);
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Please enter an email");
      return;
    }

    // minimal email validation (UI-side)
    if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      toast.error("Please enter a valid email");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const result = await register(formData.username.trim(), formData.email.trim(), formData.password);

      if (result?.success) {
        toast.success("Account created!");
        navigate("/dashboard");
      } else {
        toast.error(result?.message || "Register failed.");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="auth-page min-h-screen bg-[#050613] px-4 py-8 sm:px-6 lg:px-8"
      style={{
        backgroundImage: `url(${dashboardBG})`,
        backgroundPosition: "bottom center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-10 lg:grid-cols-2">

        {/* LEFT CARD */}
        <div
          className="auth-card-shell mx-auto w-full max-w-md border border-[#B8B8B847] bg-[#212A5533] backdrop-blur-sm animate-fadeInSoft"
          style={{ animationDelay: "60ms" }}
        >
          <div
            className="auth-card-panel border border-[#EBEBEB1C] bg-[#FFFFFF14] p-6 shadow-[0_25px_50px_rgba(3,8,25,0.55)] backdrop-blur animate-fadeInSoft"
            style={{ animationDelay: "120ms" }}
          >

            <div className="mb-6 flex items-center justify-between animate-fadeInSoft" style={{ animationDelay: "160ms" }}>
              <h1 className="text-5xl leading-[1.2] pb-1 bg-gradient-to-r from-[#FFFFFF66] via-white to-[#FFFFFF66] bg-clip-text text-transparent">
                Register
              </h1>
              <div className="shrink-0 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  aria-label="Go back"
                  className="inline-flex items-center justify-center p-2 text-white/70 transition hover:text-white"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15 18l-6-6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <Link
                  to="/"
                  aria-label="Go to landing page"
                  className="inline-flex transform-gpu transition-all duration-300 hover:scale-[1.04] hover:drop-shadow-[0_14px_30px_rgba(72,111,255,0.35)]"
                >
                  <img src={logo} alt="logo" className="h-12 w-auto" />
                </Link>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Username */}
              <div>
                <label className="mb-2 block text-[#FFFFFF]">
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Type Username"
                  maxLength={USERNAME_MAX}
                  autoComplete="username"
                  className="rdp-input bg-[#04061640] border border-[#EBEBEB1C] placeholder:text-[#FFFFFF78] py-4"
                  required
                />
                <p className="mt-1 text-xs text-white/50">3–{USERNAME_MAX} characters</p>
              </div>

              {/* Email */}
              <div>
                <label className="mb-2 block text-[#FFFFFF]">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Type Email"
                  className="rdp-input bg-[#04061640] border border-[#EBEBEB1C] placeholder:text-[#FFFFFF78] py-4"
                  required
                />
              </div>

              {/* Password */}
              <PasswordInput
                label="Password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Type Password"
                autoComplete="new-password"
                inputClassName="rdp-input w-full bg-[#04061640] border border-[#EBEBEB1C] placeholder:text-[#FFFFFF78] py-4 pr-11"
                required
              />

              {/* Confirm Password */}
              <PasswordInput
                label="Confirm Password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-type Password"
                autoComplete="new-password"
                inputClassName="rdp-input w-full bg-[#04061640] border border-[#EBEBEB1C] placeholder:text-[#FFFFFF78] py-4 pr-11"
                required
              />

              <button
                type="submit"
                disabled={loading}
                className="mt-1 block w-[80%] mx-auto bg-[#FFFFFF0F] rounded-xl py-2.5 text-sm font-medium text-white
                shadow-[0_2px_4px_#00000040,inset_0_1px_1px_#FFFFFF3B,inset_0_-16px_28px_#FFFFFF26]
                transition-all duration-300 hover:bg-[#FFFFFF14] hover:shadow-[0_10px_22px_#00000050,inset_0_1px_1px_#FFFFFF4D,inset_0_-16px_28px_#FFFFFF2E]
                disabled:opacity-50"
              >
                {loading ? "Creating Account..." : "Register"}
              </button>

            </form>

            <p className="mt-5 text-center text-sm text-white">
              Already have account?{" "}
              <Link to="/signin" className="font-semibold text-[#486FFF]">
                Log In
              </Link>
            </p>

          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="hidden lg:block animate-fadeInSoft" style={{ animationDelay: "220ms" }}>

          <h2 className="text-5xl font-light leading-tight text-white animate-fadeInSoft" style={{ animationDelay: "260ms" }}>
            <span className="font-semibold">Enterprise</span> RDP
            <br />
            Powered by <span className="font-semibold">AMD Ryzen 9</span>
          </h2>

          <p className="mt-5 max-w-xl text-[#9B9DA7] font-inter animate-fadeInSoft" style={{ animationDelay: "320ms" }}>
            Create your account and deploy instantly on premium Ryzen 9 hardware
            with low-latency routing and reliable infrastructure across regions.
          </p>

          <div className="relative mt-10 w-fit animate-fadeInSoft" style={{ animationDelay: "380ms" }}>
            <img src={worldmap} alt="world" className="animate-fadeInSoft" style={{ animationDelay: "420ms" }} />

            {/* USA */}
            <div className="absolute top-[35%] left-[25%] -translate-x-1/2 -translate-y-1/2 flex items-center gap-3 bg-[#FFFFFF1A] py-2 px-4 rounded-xl backdrop-blur-[4px] shadow-[0_2px_4px_#00000040,inset_0_1px_1px_#FFFFFF3B,inset_0_-16px_28px_#FFFFFF26] animate-fadeInSoft" style={{ animationDelay: "480ms" }}>
              <div className="rounded-full border border-[#C0C0C0] flex items-center justify-center">
                <img src={usa} alt="usa" />
              </div>
              <p className="text-xl text-white">USA</p>
            </div>

            {/* NL */}
            <div className="absolute top-[30%] left-[50%] -translate-x-1/2 -translate-y-1/2 flex items-center gap-3 bg-[#FFFFFF1A] py-2 px-4 rounded-xl backdrop-blur-[4px] shadow-[0_2px_4px_#00000040,inset_0_1px_1px_#FFFFFF3B,inset_0_-16px_28px_#FFFFFF26] animate-fadeInSoft" style={{ animationDelay: "540ms" }}>
              <div className="rounded-full border border-[#C0C0C0] flex items-center justify-center">
                <img src={nl} alt="nl" />
              </div>
              <p className="text-xl text-white">NL</p>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default SignUp;